// frontend/src/pages/admin/CustomerReviews.jsx
// ============================================
// ADMIN: Customer Reviews
// View all reviews — hide/unhide only, no delete
// Filters: rating, oasis, package, visibility
// Search: customer name or review text
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import { getAllReviewsAdmin, updateReviewStatus } from '../../services/reviews';
import './CustomerReviews.css';

// ─── Star display ────────────────────────────────────────────────────────────
function StarDisplay({ rating }) {
  return (
    <span className="cr-stars" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((s) => (
        <span key={s} style={{ color: s <= rating ? '#f59e0b' : '#d1d5db' }}>★</span>
      ))}
    </span>
  );
}

// ─── Media thumbnails + lightbox ─────────────────────────────────────────────
function MediaPreview({ photos, video }) {
  const [lightbox, setLightbox] = useState(null); // { url, type }

  const allMedia = [
    ...(photos || []).map((p) => ({ url: p.url, type: 'image' })),
    ...(video?.url ? [{ url: video.url, type: 'video' }] : []),
  ];

  if (allMedia.length === 0) return <span className="cr-no-media">—</span>;

  return (
    <>
      <div className="cr-media-row">
        {allMedia.map((m, i) => (
          <button
            key={i}
            className="cr-media-thumb"
            onClick={() => setLightbox(m)}
            aria-label={m.type === 'video' ? 'Play video' : 'View photo'}
          >
            {m.type === 'video' ? (
              <span className="cr-video-icon">▶</span>
            ) : (
              <img src={m.url} alt="" />
            )}
          </button>
        ))}
      </div>

      {lightbox && (
        <div className="cr-lightbox" onClick={() => setLightbox(null)}>
          <div className="cr-lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <button
              className="cr-lightbox-close"
              onClick={() => setLightbox(null)}
              aria-label="Close"
            >
              ✕
            </button>
            {lightbox.type === 'video' ? (
              <video
                src={lightbox.url}
                controls
                autoPlay
                className="cr-lightbox-media"
              />
            ) : (
              <img src={lightbox.url} alt="" className="cr-lightbox-media" />
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function CustomerReviews() {
  const PAGE_SIZE = 10;
  const [reviews, setReviews]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [togglingId, setTogglingId] = useState(null); // review id currently being toggled
  const [currentPage, setCurrentPage] = useState(1);

  // Filters
  const [search, setSearch]               = useState('');
  const [filterRating, setFilterRating]   = useState('');
  const [filterOasis, setFilterOasis]     = useState('');
  const [filterPackage, setFilterPackage] = useState('');
  const [filterStatus, setFilterStatus]   = useState(''); // '' | 'approved' | 'hidden'

  // ── Load reviews ──────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllReviewsAdmin();
      if (data.success) {
        setReviews(data.reviews || []);
      } else {
        setError(data.message || 'Failed to load reviews.');
      }
    } catch {
      setError('Failed to load reviews. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Toggle hide/unhide ────────────────────────────────────────────────────
  const handleToggleVisibility = async (review) => {
    const nextStatus = review.status === 'approved' ? 'hidden' : 'approved';
    setTogglingId(review._id);
    try {
      const data = await updateReviewStatus(review._id, nextStatus);
      if (data.success) {
        setReviews((prev) =>
          prev.map((r) => (r._id === review._id ? { ...r, status: nextStatus } : r))
        );
      } else {
        alert(data.message || 'Failed to update review visibility.');
      }
    } catch {
      alert('Failed to update review visibility. Please try again.');
    } finally {
      setTogglingId(null);
    }
  };

  // ── Derived data ──────────────────────────────────────────────────────────
  const packageOptions = [...new Set(reviews.map((r) => r.package).filter(Boolean))].sort();

  const filtered = reviews.filter((r) => {
    if (filterRating  && r.rating !== parseInt(filterRating)) return false;
    if (filterOasis   && r.oasis !== filterOasis)             return false;
    if (filterPackage && r.package !== filterPackage)         return false;
    if (filterStatus  && r.status !== filterStatus)           return false;
    if (search) {
      const q    = search.toLowerCase();
      const name = r.isAnonymous ? 'anonymous' : (r.customerName || '').toLowerCase();
      const text = (r.text || '').toLowerCase();
      if (!name.includes(q) && !text.includes(q)) return false;
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginatedReviews = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterRating, filterOasis, filterPackage, filterStatus]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const avgRating = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : '—';

  const hiddenCount   = reviews.filter((r) => r.status === 'hidden').length;
  const visibleCount  = reviews.filter((r) => r.status === 'approved').length;

  const clearFilters = () => {
    setSearch('');
    setFilterRating('');
    setFilterOasis('');
    setFilterPackage('');
    setFilterStatus('');
  };

  const hasFilters = search || filterRating || filterOasis || filterPackage || filterStatus;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="cr-page">

      {/* ── Header ── */}
      <div className="cr-header">
        <div>
          <h1 className="cr-title">Customer Reviews</h1>
          <p className="cr-subtitle">Manage visibility of guest reviews</p>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="cr-stats">
        <div className="cr-stat-card">
          <span className="cr-stat-value">{reviews.length}</span>
          <span className="cr-stat-label">Total Reviews</span>
        </div>
        <div className="cr-stat-card cr-stat-card--yellow">
          <span className="cr-stat-value">{avgRating}</span>
          <span className="cr-stat-label">Avg Rating</span>
        </div>
        <div className="cr-stat-card cr-stat-card--green">
          <span className="cr-stat-value">{visibleCount}</span>
          <span className="cr-stat-label">Visible</span>
        </div>
        <div className="cr-stat-card cr-stat-card--slate">
          <span className="cr-stat-value">{hiddenCount}</span>
          <span className="cr-stat-label">Hidden</span>
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className="cr-toolbar">
        <div className="cr-search-wrap">
          <i className="fas fa-search cr-search-icon" />
          <input
            className="cr-search"
            type="text"
            placeholder="Search by customer name or review text…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <select
          className="cr-filter-select"
          value={filterRating}
          onChange={(e) => setFilterRating(e.target.value)}
        >
          <option value="">All Ratings</option>
          {[5, 4, 3, 2, 1].map((s) => (
            <option key={s} value={s}>{s} Stars</option>
          ))}
        </select>

        <select
          className="cr-filter-select"
          value={filterOasis}
          onChange={(e) => setFilterOasis(e.target.value)}
        >
          <option value="">All Oasis</option>
          <option value="Oasis 1">Oasis 1</option>
          <option value="Oasis 2">Oasis 2</option>
        </select>

        <select
          className="cr-filter-select"
          value={filterPackage}
          onChange={(e) => setFilterPackage(e.target.value)}
        >
          <option value="">All Packages</option>
          {packageOptions.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>

        <select
          className="cr-filter-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="approved">Visible</option>
          <option value="hidden">Hidden</option>
        </select>

        {hasFilters && (
          <button className="cr-clear-btn" onClick={clearFilters}>
            <i className="fas fa-times" /> Clear
          </button>
        )}
      </div>

      {/* ── Result count ── */}
      {!loading && !error && (
        <p className="cr-result-count">
          {filtered.length === reviews.length
            ? `${reviews.length} review${reviews.length !== 1 ? 's' : ''}`
            : `${filtered.length} of ${reviews.length} review${reviews.length !== 1 ? 's' : ''}`}
        </p>
      )}

      {/* ── States: loading / error / empty / table ── */}
      {loading ? (
        <div className="cr-loading">
          <div className="cr-spinner" />
          <p>Loading reviews…</p>
        </div>
      ) : error ? (
        <div className="cr-error">
          <i className="fas fa-exclamation-circle" />
          <p>{error}</p>
          <button className="cr-retry-btn" onClick={load}>Retry</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="cr-empty">
          <i className="fas fa-star cr-empty-icon" />
          <p>{hasFilters ? 'No reviews match your filters.' : 'No reviews yet.'}</p>
          {hasFilters && (
            <button className="cr-clear-btn" onClick={clearFilters}>Clear filters</button>
          )}
        </div>
      ) : (
        <div className="cr-table-wrap">
          <table className="cr-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Rating</th>
                <th>Review</th>
                <th>Photos / Video</th>
                <th>Date</th>
                <th>Oasis</th>
                <th>Package</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedReviews.map((review) => {
                const isHidden    = review.status === 'hidden';
                const isToggling  = togglingId === review._id;

                return (
                  <tr key={review._id} className={isHidden ? 'cr-row--hidden' : ''}>

                    {/* Customer */}
                    <td>
                      <div className="cr-customer">
                        {review.isAnonymous ? (
                          <span className="cr-customer-name cr-anon">Anonymous</span>
                        ) : (
                          <span className="cr-customer-name">{review.customerName}</span>
                        )}
                        {!review.isAnonymous && review.customerEmail && (
                          <span className="cr-customer-email">{review.customerEmail}</span>
                        )}
                      </div>
                    </td>

                    {/* Rating */}
                    <td>
                      <StarDisplay rating={review.rating} />
                    </td>

                    {/* Review text */}
                    <td>
                      <p className="cr-text-preview">{review.text}</p>
                    </td>

                    {/* Media */}
                    <td>
                      <MediaPreview photos={review.photos} video={review.video} />
                    </td>

                    {/* Date */}
                    <td>
                      <span className="cr-date">
                        {new Date(review.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                    </td>

                    {/* Oasis */}
                    <td>
                      <span className="cr-oasis-badge">{review.oasis}</span>
                    </td>

                    {/* Package */}
                    <td>
                      <span className="cr-pkg">{review.package}</span>
                    </td>

                    {/* Status badge */}
                    <td>
                      <span className={`cr-status-badge cr-status-badge--${isHidden ? 'hidden' : 'visible'}`}>
                        {isHidden ? (
                          <><i className="fas fa-eye-slash" /> Hidden</>
                        ) : (
                          <><i className="fas fa-eye" /> Visible</>
                        )}
                      </span>
                    </td>

                    {/* Action */}
                    <td>
                      <button
                        className={`cr-toggle-btn cr-toggle-btn--${isHidden ? 'show' : 'hide'}`}
                        onClick={() => handleToggleVisibility(review)}
                        disabled={isToggling}
                        aria-label={isHidden ? 'Make visible to customers' : 'Hide from customers'}
                      >
                        {isToggling ? (
                          <span className="cr-btn-spinner" />
                        ) : isHidden ? (
                          <><i className="fas fa-eye" /> Unhide</>
                        ) : (
                          <><i className="fas fa-eye-slash" /> Hide</>
                        )}
                      </button>
                    </td>

                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length > 0 && (
            <div className="cr-pagination">
              <span>
                Showing {(currentPage - 1) * PAGE_SIZE + 1}
                -{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}
              </span>
              <div className="cr-pagination-controls">
                <button
                  type="button"
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  Previous
                </button>
                <span>Page {currentPage} of {totalPages}</span>
                <button
                  type="button"
                  onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}